import * as React from 'react';
import { assoc, pair, fromPairs, equals } from 'ramda';
import {
  IFormValues,
  IFormIssues,
  SubmitFormType,
  ResetFormType,
  OnSubmitType,
  IErrors,
  IWarnings,
  ITouched,
  IFieldError,
  IFieldWarning,
  IFields,
  IFieldFunctions,
  SetErrorsType,
  SetWarningsType,
  SetTouchedType,
  SetValuesType,
  SetSubmittingType,
  ValidateFieldType,
  FieldValueType,
  IFieldIssues,
  SetFieldValueType,
  SetFieldTouchedType,
  IFormActions,
  IComputedProps,
  IFormBag,
  SetFormStateType,
} from '../../Types/FormTypes';
import { isFunction, isPromise } from '../../Utils/ramdaCookbook';
import { addFieldIssuesToFormIssues, mergeFormIssues } from './Utils/formUtils';
import FormContext from './Contexts/FormContext';

export interface IProps {
  /** Initial field values of the form, these values will be available to render methods component as props.values. */
  initialValues?: IFormValues;
  /** Will get most values from the form icluding state, computed props and handle function */
  children: (input: IFormBag) => React.ReactNode;
  /** Validate the form's values with a function that gets the forms values and state, should return a IFormIssue object */
  validate?: (values: IFormValues, formState: IState) => IFormIssues;
  /** Default is true. Use this option to run validations on change events and change-related methods. More specifically, when either handleChange, setFieldValue, or setValues are called. */
  validateOnChange?: boolean;
  /** Default is true. Use this option to run validations on blur events. More specifically, when either handleBlur, setFieldTouched, or setTouched are called. */
  validateOnBlur?: boolean;
  /** Default is false. Control whether the form should be reseted if the wrapped component props change (using deep equality). */
  enableReinitialize?: boolean;
  /** Default is false. Control the initial value of isValid prop prior to mount. You can also pass a function. Useful for situations when you want to enable/disable a submit and reset buttons on initial mount. */
  isInitialValid?: boolean | ((props: object) => boolean | undefined);
  /** Your form submission handler. It is passed your forms values, warnings and form actions (IFormActions) */
  onSubmit: OnSubmitType;
  //** Called when values change */
  onChange: (values: IFormValues) => void;
}

export interface IState {
  values: IFormValues;
  errors: IErrors;
  warnings: IWarnings;
  touched: ITouched;
  /** whether the form is currently validating */
  isValidating: boolean;
  /** whether the form is currently submitting */
  isSubmitting: boolean;
}

/**
 * Component that helps with creating forms and handle form validation. Inside the form you should create a couple of Field components.
 * Validation happens on two levels, form level and field level. Validation function provided to this component will validate on a form level, validation functions
 * provided to the Field component will validate on a field level. The Field will be provided a value, and handlers to change the value along with other useful props.
 * The Forms children render function will be passes a submithandler, form state and other useful props.
 *
 * The form keeps track on field value, errors, warnings and touched state(a field is touched after first onBlur on that field or when the submit function is run)
 */
class EDForm extends React.Component<IProps, IState> {
  static defaultProps: Partial<IProps> = {
    validateOnChange: true,
    validateOnBlur: true,
    isInitialValid: false,
    enableReinitialize: false,
  };

  didMount: boolean;
  fields: IFields;
  initialValues: IFormValues;

  constructor(props: IProps) {
    super(props);

    const initialValues =
      props.initialValues != null ? props.initialValues : {};

    this.state = {
      values: initialValues,
      errors: {},
      warnings: {},
      touched: {},
      isValidating: false,
      isSubmitting: false,
    };

    this.didMount = false;
    this.fields = {};
    this.initialValues = initialValues;
  }

  componentDidMount() {
    this.didMount = true;
  }

  componentDidUpdate(prevProps: IProps) {
    // If the initialValues change, reset the form
    if (
      this.props.enableReinitialize &&
      !equals(prevProps.initialValues, this.props.initialValues)
    ) {
      this.initialValues =
        this.props.initialValues != null ? this.props.initialValues : {};
      this.resetForm(this.props.initialValues);
    }
  }

  componentWillUnmount() {
    this.didMount = false;
  }

  registerField = (name: string, functions: IFieldFunctions) => {
    this.fields[name] = functions;
  };

  unregisterField = (name: string) => {
    delete this.fields[name];
  };

  setErrors: SetErrorsType = (errors: IErrors): void => {
    this.setState({ errors });
  };

  setWarnings: SetWarningsType = (warnings: IWarnings): void => {
    this.setState({ warnings });
  };

  setTouched: SetTouchedType = (touched: ITouched) => {
    this.setState({ touched }, () => {
      if (this.props.validateOnBlur) {
        this.runValidations(this.state.values);
      }
    });
  };

  setValues: SetValuesType = (values: IFormValues) => {
    this.setState({ values }, () => {
      if (this.props.validateOnChange) {
        this.runValidations(values);
      }
      if (this.props.onChange) {
        this.props.onChange(this.state.values);
      }
    });
  };

  setSubmitting: SetSubmittingType = (isSubmitting: boolean) => {
    this.setState({ isSubmitting });
  };

  /**
   * Run field level validation
   */
  validateField: ValidateFieldType = (fieldName: string) => {
    this.setState({ isValidating: true });
    this.runSingleFieldLevelValidation(
      fieldName,
      this.state.values[fieldName],
      this.state
    ).then(error => {
      if (this.didMount) {
        this.setState({
          errors: assoc(fieldName, error, this.state.errors),
          isValidating: false,
        });
      }
    });
  };

  runSingleFieldLevelValidation = (
    field: string,
    value: FieldValueType,
    formState: IState
  ): Promise<IFieldIssues> => {
    return new Promise(resolve => {
      const resolveField = this.fields[field];
      if (resolveField.validate) {
        resolve(resolveField.validate(value, formState));
      }
    });
  };

  runFieldLevelValidations(values: IFormValues): Promise<IFormIssues> {
    const fieldKeysWithValidation: string[] = Object.keys(this.fields).filter(
      f =>
        this.fields &&
        this.fields[f] &&
        this.fields[f].validate &&
        isFunction(this.fields[f].validate)
    );

    if (fieldKeysWithValidation.length === 0) {
      return Promise.resolve({ errors: {}, warnings: {} });
    }

    // Update values on state
    const newState = assoc('values', values, this.state);

    // Construct an array with all of the field validation functions
    const fieldValidations: Array<Promise<IFieldIssues>> =
      fieldKeysWithValidation.map(
        key =>
          this.runSingleFieldLevelValidation(key, values[key], newState).then(
            x => x,
            e => e
          ) // always catch so Promise.all runs each one
      );

    return Promise.all(fieldValidations).then(
      (fieldErrorsList: IFieldIssues[]) =>
        fieldErrorsList.reduce(
          (prev, curr, index) => {
            let errors = prev.errors;
            let warnings = prev.warnings;
            if (curr != null) {
              errors = addFieldIssuesToFormIssues(
                fieldKeysWithValidation[index],
                curr.errors,
                prev.errors
              );
              warnings = addFieldIssuesToFormIssues(
                fieldKeysWithValidation[index],
                curr.warnings,
                prev.warnings
              );
            }

            return {
              errors,
              warnings,
            };
          },
          { errors: {}, warnings: {} } as IFormIssues
        )
    );
  }

  runValidateHandler(values: IFormValues): Promise<IFormIssues> {
    const newState = assoc('values', values, this.state);
    return new Promise(resolve => {
      const maybePromisedErrors = this.props.validate
        ? this.props.validate(values, newState)
        : undefined;
      if (isPromise(maybePromisedErrors)) {
        (maybePromisedErrors as Promise<IFormIssues>).then(
          () => {
            resolve({ errors: {}, warnings: {} });
          },
          errors => {
            resolve(errors);
          }
        );
      } else {
        resolve(maybePromisedErrors ?? { errors: {}, warnings: {} });
      }
    });
  }

  /**
   * Run all validation methods and update state accordingly
   */
  runValidations = (
    values: IFormValues = this.state.values
  ): Promise<IFormIssues> => {
    this.setState({ isValidating: true });
    return Promise.all([
      this.runFieldLevelValidations(values),
      this.props.validate
        ? this.runValidateHandler(values)
        : { errors: {}, warnings: {} },
    ]).then(([fieldIssues, handlerIssues]) => {
      const combinedIssues = mergeFormIssues(fieldIssues, handlerIssues);

      if (this.didMount) {
        this.setState({
          isValidating: false,
          errors: combinedIssues.errors,
          warnings: combinedIssues.warnings,
        });
      }

      return combinedIssues;
    });
  };

  setFieldValue: SetFieldValueType = (
    field: string,
    value: FieldValueType,
    shouldValidate: boolean = true
  ) => {
    // Set form field by name
    this.setState(
      prevState => ({
        ...prevState,
        values: assoc(field, value, prevState.values),
      }),
      () => {
        if (this.props.validateOnChange && shouldValidate) {
          this.runValidations(this.state.values);
        }
        if (this.props.onChange) {
          this.props.onChange(this.state.values);
        }
      }
    );
  };

  submitForm: SubmitFormType = () => {
    // Set all values to touched
    this.setState({
      touched: fromPairs(
        Object.keys(this.state.values).map(key => pair(key, true))
      ),
      isSubmitting: true,
    });

    this.runValidations().then(combinedIssues => {
      const isValid = Object.keys(combinedIssues.errors).length === 0;
      if (isValid) {
        this.executeSubmit(combinedIssues.warnings);
      } else {
        this.setState({ isSubmitting: false });
      }
    });
  };

  executeSubmit = (warnings: IWarnings) => {
    this.props.onSubmit(this.state.values, warnings, this.getFormActions());
  };

  handleChange = (name: string, value: FieldValueType) => {
    this.setState(
      prevState => ({
        ...prevState,
        values: assoc(name, value, prevState.values),
      }),
      () => {
        if (this.props.validateOnChange) {
          this.runValidations(this.state.values);
        }
        if (this.props.onChange) {
          this.props.onChange(this.state.values);
        }
      }
    );
  };

  handleBlur = (name: string, value?: FieldValueType) => {
    const newValues =
      value != null ? assoc(name, value, this.state.values) : this.state.values;

    this.setState(prevState => ({
      touched: assoc(name, true, prevState.touched),
    }));

    if (this.props.validateOnBlur) {
      this.runValidations(newValues);
    }
  };

  setFieldTouched: SetFieldTouchedType = (
    fieldName: string,
    touched: boolean = true,
    shouldValidate: boolean = true
  ) => {
    this.setState(
      prevState => ({
        ...prevState,
        touched: assoc(fieldName, touched, prevState.touched),
      }),
      () => {
        if (this.props.validateOnBlur && shouldValidate) {
          this.runValidations(this.state.values);
        }
      }
    );
  };

  setFieldErrors = (fieldName: string, fieldErrors: IFieldError[]) => {
    this.setState({
      errors: assoc(fieldName, fieldErrors, this.state.errors),
    });
  };

  setFieldWarnings = (fieldName: string, fieldWarnings: IFieldWarning[]) => {
    this.setState({
      warnings: assoc(fieldName, fieldWarnings, this.state.warnings),
    });
  };

  resetForm: ResetFormType = (nextValues?: IFormValues) => {
    const values = nextValues
      ? nextValues
      : this.props.initialValues
      ? this.props.initialValues
      : {};

    this.initialValues = values;

    this.setState({
      isSubmitting: false,
      isValidating: false,
      errors: {},
      warnings: {},
      touched: {},
      values,
    });
  };

  handleReset = () => {
    this.resetForm();
  };

  setFormState: SetFormStateType = <K extends keyof IState>(
    state:
      | ((
          prevState: Readonly<IState>,
          props: Readonly<IProps>
        ) => Pick<IState, K> | null)
      | (Pick<IState, K> | null),
    callback?: () => void
  ) => this.setState(state, callback);

  getFormActions = (): IFormActions => {
    return {
      resetForm: this.resetForm,
      submitForm: this.submitForm,
      validateForm: this.runValidations,
      validateField: this.validateField,
      setErrors: this.setErrors,
      setWarnings: this.setWarnings,
      setFieldErrors: this.setFieldErrors,
      setFieldWarnings: this.setFieldWarnings,
      setFieldTouched: this.setFieldTouched,
      setFieldValue: this.setFieldValue,
      setSubmitting: this.setSubmitting,
      setTouched: this.setTouched,
      setValues: this.setValues,
      setFormState: this.setFormState,
    };
  };

  getFormComputedProps = (): IComputedProps => {
    const { isInitialValid } = this.props;
    const dirty = !equals(this.initialValues, this.state.values);
    return {
      dirty,
      isValid: dirty
        ? this.state.errors && Object.keys(this.state.errors).length === 0
        : isInitialValid !== false && isFunction(isInitialValid)
        ? Boolean(isInitialValid(this.props))
        : Boolean(isInitialValid),
      initialValues: this.initialValues,
    };
  };

  getFormBag = (): IFormBag => ({
    ...this.state,
    ...this.getFormActions(),
    ...this.getFormComputedProps(),
    registerField: this.registerField,
    unregisterField: this.unregisterField,
    handleBlur: this.handleBlur,
    handleChange: this.handleChange,
    handleReset: this.handleReset,
    validateOnChange: this.props.validateOnChange,
    validateOnBlur: this.props.validateOnBlur,
  });

  render() {
    const formBag = this.getFormBag();
    return (
      <FormContext.Provider value={formBag}>
        <form>{this.props.children(formBag)}</form>
      </FormContext.Provider>
    );
  }
}

export default EDForm;
